import { useEffect, useState, type HTMLAttributes, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { HexColorPicker } from "react-colorful";
import { Trash2, Eye, EyeOff, Check, Copy, Sparkles, GripVertical, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MeshBackground } from "@/components/marketing/MeshBackground";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "@/styles/marketing.css";
import {
  DEFAULT_MESH_CONFIG,
  MESH_STARTERS,
  activateMeshPreset,
  createMeshPreset,
  defaultAnchorPosition,
  deleteMeshPreset,
  encodePreviewToHash,
  getPreviewMeshConfig,
  listMeshPresets,
  meshPreviewBus,
  setPreviewMeshConfig,
  speedToDurationSec,
  updateMeshPreset,
  type MeshBlendMode,
  type MeshConfig,
  type MeshMobileOverride,

  type MeshGradientType,
  type MeshMotion,
  type MeshPreset,
  type MeshRenderer,
  type MeshSlot,
  type MeshWebglAnchor,
} from "@/lib/meshGradient";
import {
  DEFAULT_SHAPE_MASK,
  MAX_CUSTOM_SVG_BYTES,
  SHAPE_MODE_OPTIONS,
  SHAPE_PRESET_OPTIONS,
  sanitizeSvg,
  type ShapeMaskConfig,
  type ShapeMaskMode,
  type ShapeMaskPreset,
} from "@/lib/meshShapeMask";


const cloneConfig = (c: MeshConfig): MeshConfig => JSON.parse(JSON.stringify(c)) as MeshConfig;

const BLEND_OPTIONS: MeshBlendMode[] = ["multiply", "screen", "overlay", "soft-light", "plus-lighter", "lighten", "darken", "normal"];
const SHAPE_OPTIONS: MeshGradientType[] = ["radial", "linear", "conic"];

const ColorSwatch = ({ hex, onChange }: { hex: string; onChange: (h: string) => void }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        className="h-8 w-8 shrink-0 rounded border border-border"
        style={{ background: hex }}
        aria-label="Color"
      />
    </PopoverTrigger>
    <PopoverContent className="w-56 space-y-2">
      <HexColorPicker color={hex} onChange={onChange} style={{ width: "100%" }} />
      <Input value={hex} onChange={(e) => onChange(e.target.value)} />
    </PopoverContent>
  </Popover>
);

/** Sortable wrapper — provides drag handle props to children via render-prop. */
const Sortable = ({
  id,
  children,
}: {
  id: string;
  children: (handleProps: HTMLAttributes<HTMLButtonElement>) => ReactNode;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : "auto" as const,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ ...attributes, ...listeners })}
    </div>
  );
};

const DragHandle = (props: HTMLAttributes<HTMLButtonElement>) => (
  <button
    type="button"
    aria-label="Drag to reorder"
    className="cursor-grab touch-none text-muted-foreground hover:text-foreground"
    {...props}
  >
    <GripVertical className="h-4 w-4" />
  </button>
);

export const MeshGradientTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [presets, setPresets] = useState<MeshPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(() => searchParams.get("preset"));
  const [editing, setEditing] = useState<MeshConfig>(DEFAULT_MESH_CONFIG);
  const [editingName, setEditingName] = useState("New preset");
  const [dirty, setDirty] = useState(false);
  const [previewLive, setPreviewLive] = useState<boolean>(() => getPreviewMeshConfig() !== null);
  const [previewDevice, setPreviewDevice] = useState<"desktop" | "tablet" | "mobile">("desktop");
  const [saving, setSaving] = useState(false);

  /** Refresh preset list. If preferId is provided, load that preset after fetch
   *  (used after create/save so a server refresh doesn't snap back to the
   *  active/first preset). Otherwise honors current selectedId, then URL
   *  ?preset=, then the active preset, then the first preset. */
  const refresh = async (preferId?: string) => {
    setLoading(true);
    try {
      const rows = await listMeshPresets();
      setPresets(rows);
      const urlPreset = searchParams.get("preset");
      const desiredId = preferId ?? selectedId ?? urlPreset;
      const desired = desiredId ? rows.find((r) => r.id === desiredId) : undefined;
      if (desired) {
        loadPreset(desired);
      } else if (!selectedId && rows.length > 0) {
        const active = rows.find((r) => r.is_active) ?? rows[0];
        loadPreset(active);
      }
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep ?preset=<id> in the URL in sync with the selected preset, so a
  // remount (Vite restart, tab nav, etc.) restores the same selection.
  useEffect(() => {
    const current = searchParams.get("preset");
    if (selectedId && current !== selectedId) {
      setSearchParams((sp) => { sp.set("preset", selectedId); return sp; }, { replace: true });
    } else if (!selectedId && current) {
      setSearchParams((sp) => { sp.delete("preset"); return sp; }, { replace: true });
    }
  }, [selectedId, searchParams, setSearchParams]);

  useEffect(() => {
    if (previewLive) {
      const ok = setPreviewMeshConfig(editing);
      if (!ok) {
        setPreviewLive(false);
        toast.error("Preview disabled — config too large for this browser's storage.");
      }
    }
  }, [editing, previewLive]);

  // Reply to marketing tabs that just opened and ping for the current preview.
  useEffect(() => {
    const unsub = meshPreviewBus.subscribe((msg) => {
      if (msg.kind === "request" && previewLive) {
        meshPreviewBus.publish(editing);
      }
    });
    return unsub;
  }, [editing, previewLive]);

  const loadPreset = (p: MeshPreset) => {
    setSelectedId(p.id);
    setEditingName(p.name);
    setEditing(cloneConfig(p.config));
    setDirty(false);
  };

  const startNew = () => {
    setSelectedId(null);
    setEditingName("New preset");
    setEditing(cloneConfig(DEFAULT_MESH_CONFIG));
    setDirty(true);
  };

  const loadStarter = (id: string) => {
    const s = MESH_STARTERS.find((x) => x.id === id);
    if (!s) return;
    setSelectedId(null);
    setEditingName(s.name);
    setEditing(cloneConfig(s.config));
    setDirty(true);
    toast.info(`Loaded "${s.name}" — save as a new preset to keep it.`);
  };

  const patchEditing = (patch: Partial<MeshConfig>) => {
    setEditing((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };
  const patchSlot = (i: number, patch: Partial<MeshSlot>) => {
    setEditing((prev) => ({
      ...prev,
      slots: prev.slots.map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
    }));
    setDirty(true);
  };
  const patchWebgl = (patch: Partial<MeshConfig["webgl"]>) => {
    setEditing((prev) => ({ ...prev, webgl: { ...prev.webgl, ...patch } }));
    setDirty(true);
  };
  const patchWebglAnchor = (i: number, patch: Partial<MeshWebglAnchor>) => {
    setEditing((prev) => {
      const colors = prev.webgl.colors.map((a, idx) => (idx === i ? { ...a, ...patch } : a));
      return { ...prev, webgl: { ...prev.webgl, colors } };
    });
    setDirty(true);
  };
  const resetWebglAnchorPosition = (i: number) => {
    const def = defaultAnchorPosition(i);
    // Shader flips y internally; admin y is 0=top, 1=bottom — match what the canvas shows.
    patchWebglAnchor(i, { x: def.x, y: 1 - def.y });
  };
  const patchGrain = (patch: Partial<MeshConfig["grain"]>) => {
    setEditing((prev) => ({ ...prev, grain: { ...prev.grain, ...patch } }));
    setDirty(true);
  };
  const DEFAULT_MOBILE_OVERRIDE: MeshMobileOverride = {
    enabled: false,
    baseColor: "#FFF5EC",
    blend: "screen",
    opacityMultiplier: 0.7,
    blurDelta: 0,
  };
  const patchMobile = (patch: Partial<MeshMobileOverride>) => {
    setEditing((prev) => ({
      ...prev,
      mobile: { ...DEFAULT_MOBILE_OVERRIDE, ...(prev.mobile ?? {}), ...patch },
    }));
    setDirty(true);
  };

  const patchShape = (patch: Partial<ShapeMaskConfig>) => {
    setEditing((prev) => {
      const current = prev.shape ?? DEFAULT_SHAPE_MASK;
      return { ...prev, shape: { ...current, ...patch, halo: { ...current.halo, ...(patch.halo ?? {}) } } };
    });
    setDirty(true);
  };

  const handleSvgUpload = (file: File) => {
    if (!file.name.toLowerCase().endsWith(".svg") && file.type !== "image/svg+xml") {
      toast.error("Please upload a .svg file");
      return;
    }
    if (file.size > MAX_CUSTOM_SVG_BYTES) {
      toast.error(`SVG too large — max ${Math.round(MAX_CUSTOM_SVG_BYTES / 1000)} KB`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result ?? "");
      const cleaned = sanitizeSvg(raw);
      if (!/<svg[\s\S]*<\/svg>/i.test(cleaned)) {
        toast.error("File doesn't look like a valid SVG");
        return;
      }
      patchShape({ preset: "custom", customSvg: cleaned });
      toast.success("Custom SVG loaded");
    };
    reader.onerror = () => toast.error("Couldn't read SVG file");
    reader.readAsText(file);
  };

  // Drag-and-drop sensors — shared by slots and webgl color reordering.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleSlotsDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = Number(String(active.id).replace("slot-", ""));
    const to = Number(String(over.id).replace("slot-", ""));
    if (Number.isNaN(from) || Number.isNaN(to)) return;
    setEditing((prev) => ({ ...prev, slots: arrayMove(prev.slots, from, to) }));
    setDirty(true);
  };

  const handleWebglDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = Number(String(active.id).replace("wgl-", ""));
    const to = Number(String(over.id).replace("wgl-", ""));
    if (Number.isNaN(from) || Number.isNaN(to)) return;
    setEditing((prev) => {
      const next = arrayMove([...prev.webgl.colors], from, to);
      return { ...prev, webgl: { ...prev.webgl, colors: next } };
    });
    setDirty(true);
  };



  const save = async () => {
    if (!editingName.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      let keepId = selectedId;
      if (selectedId) {
        await updateMeshPreset(selectedId, { name: editingName.trim(), config: editing });
        toast.success("Preset saved");
      } else {
        const created = await createMeshPreset(editingName.trim(), editing);
        setSelectedId(created.id);
        keepId = created.id;
        toast.success("Preset created");
      }
      setDirty(false);
      await refresh(keepId ?? undefined);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const activate = async (id: string) => {
    try {
      await activateMeshPreset(id);
      toast.success("Preset activated — live on marketing pages");
      setPreviewLive(false);
      setPreviewMeshConfig(null);
      await refresh(selectedId ?? id);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const duplicate = async (p: MeshPreset) => {
    try {
      const created = await createMeshPreset(`${p.name} copy`, cloneConfig(p.config));
      toast.success("Duplicated");
      await refresh();
      loadPreset(created);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const remove = async (p: MeshPreset) => {
    if (p.is_active) { toast.error("Deactivate or activate another preset first"); return; }
    if (!confirm(`Delete preset "${p.name}"?`)) return;
    try {
      await deleteMeshPreset(p.id);
      if (selectedId === p.id) startNew();
      toast.success("Deleted");
      await refresh();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const togglePreview = (on: boolean) => {
    const ok = setPreviewMeshConfig(on ? editing : null);
    if (!ok) {
      toast.error("Couldn't store preview locally — the config (likely a large embedded SVG) exceeds this browser's storage limit.");
      setPreviewLive(false);
      return;
    }
    setPreviewLive(on);
    if (on) toast.success("Preview enabled — open or refresh a marketing tab in this browser to see it.");
  };

  const isWebgl = editing.renderer === "webgl-mesh";

  const presetsCard = (
      <Card className="shadow-soft">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Saved presets</CardTitle>
          <Button size="sm" variant="outline" onClick={startNew}>New</Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Starter dropdown */}
          <div>
            <Label className="text-xs flex items-center gap-1"><Sparkles className="h-3 w-3" /> Load starter</Label>
            <Select onValueChange={loadStarter}>
              <SelectTrigger><SelectValue placeholder="Choose a starter…" /></SelectTrigger>
              <SelectContent>
                {MESH_STARTERS.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-[11px] text-muted-foreground">Loads into editor — save to keep.</p>
          </div>

          <div className="border-t border-border pt-3 space-y-2">
            {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {!loading && presets.length === 0 && (
              <p className="text-sm text-muted-foreground">No presets yet. Save your first one on the right.</p>
            )}
            {presets.map((p) => {
              const isWebglPreset = p.config.renderer === "webgl-mesh";
              const chipHexes = isWebglPreset
                ? p.config.webgl.colors.slice(0, 8).map((a) => a.hex)
                : p.config.slots.filter((s) => s.enabled).slice(0, 8).map((s) => s.hex);
              return (
              <div
                key={p.id}
                className={`rounded-md border p-2 ${selectedId === p.id ? "border-primary" : "border-border"}`}
              >
                <button type="button" className="w-full text-left" onClick={() => loadPreset(p)}>
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">{p.name}</span>
                    <Badge variant="outline" className="ml-auto text-[10px] uppercase tracking-wide">
                      {isWebglPreset ? "WebGL" : "CSS"}
                    </Badge>
                    {p.is_active && <Badge variant="secondary">Live</Badge>}
                  </div>
                  <div
                    className="mt-2 flex h-5 gap-0.5 overflow-hidden rounded border border-border/60"
                    style={{ background: p.config.baseColor || "transparent" }}
                  >
                    {chipHexes.length === 0 ? (
                      <div className="flex-1 text-[10px] text-muted-foreground flex items-center justify-center">
                        no colors
                      </div>
                    ) : (
                      chipHexes.map((hex, i) => (
                        <div
                          key={i}
                          className="flex-1 border-r border-border/40 last:border-r-0"
                          style={{ background: hex }}
                          title={hex}
                        />
                      ))
                    )}
                  </div>
                </button>
                <div className="mt-2 flex gap-1">
                  {!p.is_active && (
                    <Button size="sm" variant="default" className="h-7 flex-1" onClick={() => activate(p.id)}>
                      <Check className="h-3 w-3" /> Activate
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => duplicate(p)} aria-label="Duplicate">
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => remove(p)} aria-label="Delete">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );})}
          </div>
        </CardContent>
      </Card>
  );

  const hashPayload = encodePreviewToHash(editing);
  const landingHref = hashPayload ? `/#${hashPayload}` : "/";
  const landingDisabled = !hashPayload;

  const previewCard = (
        <Card className="shadow-soft overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 flex-wrap gap-2">
            <CardTitle className="text-base">Preview</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Label htmlFor="preview-live" className="text-xs text-muted-foreground">
                Preview on marketing pages (this browser only)
              </Label>
              <Switch id="preview-live" checked={previewLive} onCheckedChange={togglePreview} />
              {previewLive ? <Eye className="h-4 w-4 text-primary" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
              <Button
                asChild={!landingDisabled}
                disabled={landingDisabled}
                size="sm"
                variant="outline"
                className="ml-2 h-7"
                title={landingDisabled
                  ? "Current config is too large to pass in a link — save the preset to preview."
                  : "Opens landing with this exact config baked into the URL."}
              >
                {landingDisabled
                  ? <span>Open Landing ↗</span>
                  : <a href={landingHref} target="_blank" rel="noreferrer">Open Landing ↗</a>}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-2 flex items-center gap-1">
              {(["desktop", "tablet", "mobile"] as const).map((d) => (
                <Button
                  key={d}
                  type="button"
                  size="sm"
                  variant={previewDevice === d ? "default" : "outline"}
                  className="h-7 px-2 text-xs capitalize"
                  onClick={() => setPreviewDevice(d)}
                >
                  {d}
                </Button>
              ))}
            </div>
            {(() => {
              // Device frame: desktop = fluid 16/9; tablet = 820x1180; mobile = 390x844.
              // Tablet/mobile are capped to the column width and scale down proportionally.
              const frameStyle: React.CSSProperties =
                previewDevice === "desktop"
                  ? { width: "100%", aspectRatio: "16/9" }
                  : previewDevice === "tablet"
                    ? { width: "min(100%, 820px)", aspectRatio: "820 / 1180", margin: "0 auto" }
                    : { width: "min(100%, 390px)", aspectRatio: "390 / 844", margin: "0 auto" };
              return (
                <div
                  data-marketing="true"
                  className="relative overflow-hidden rounded-md border border-border"
                  style={frameStyle}
                >
                  <MeshBackground config={editing} contained forceMobile={previewDevice === "mobile"} />
                  <div className="relative z-10 flex h-full flex-col items-center justify-center text-center text-foreground">
                    <p className="font-serif text-3xl font-medium">Research. Write. Publish</p>
                    <p className="font-serif text-3xl font-medium">— from one source of truth.</p>
                  </div>
                </div>
              );
            })()}
            <p className="mt-2 text-xs text-muted-foreground">
              {previewDevice === "mobile"
                ? "Mobile preview forces the CSS-blob fallback used on phones (<768px), including any Mobile overrides."
                : isWebgl
                  ? "WebGL mesh renderer — animation runs continuously."
                  : `Animation cycles every ${speedToDurationSec(editing.speed)}s.`}
              {" "}Open Landing bakes the current editor config into the URL, so the preview works across browsers and origins without saving.
            </p>
          </CardContent>
        </Card>
  );

  const editorCard = (
        <Card className="shadow-soft">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-base">Editor</CardTitle>
              {dirty && <p className="mt-1 text-xs text-amber-600">Unsaved changes</p>}
            </div>
            <Button onClick={save} disabled={saving || !dirty}>
              {saving ? "Saving…" : selectedId ? "Save changes" : "Create preset"}
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor="preset-name">Preset name</Label>
              <Input
                id="preset-name"
                value={editingName}
                onChange={(e) => { setEditingName(e.target.value); setDirty(true); }}
              />
            </div>

            {/* Renderer + globals */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <Label>Renderer</Label>
                <Select value={editing.renderer} onValueChange={(v) => patchEditing({ renderer: v as MeshRenderer })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="css-blobs">CSS blobs (8 slots)</SelectItem>
                    <SelectItem value="webgl-mesh">WebGL mesh (Stripe-style)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Motion preset</Label>
                <Select value={editing.motion} onValueChange={(v) => patchEditing({ motion: v as MeshMotion })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="drift">Drift</SelectItem>
                    <SelectItem value="swirl">Swirl</SelectItem>
                    <SelectItem value="pulse">Pulse</SelectItem>
                    <SelectItem value="still">Still</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Base color (behind everything)</Label>
                <div className="mt-1 flex items-center gap-2">
                  <ColorSwatch hex={editing.baseColor} onChange={(hex) => patchEditing({ baseColor: hex })} />
                  <Input value={editing.baseColor} onChange={(e) => patchEditing({ baseColor: e.target.value })} className="h-8 font-mono text-xs" />
                </div>
              </div>
            </div>

            {/* WebGL OR Slots */}
            {isWebgl ? (
              <div className="rounded-md border border-border p-3 space-y-3">
                <Label>WebGL mesh anchors (8)</Label>
                <p className="text-[11px] text-muted-foreground">Eight color anchors blended across a noise-warped field — the Stripe.com technique. Each anchor has its own opacity (weight), position, and reach. Drag the handle to reorder render priority. Motion preset above changes how the field animates.</p>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleWebglDragEnd}>
                  <SortableContext items={editing.webgl.colors.map((_, i) => `wgl-${i}`)} strategy={rectSortingStrategy}>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      {editing.webgl.colors.map((anchor, i) => (
                        <Sortable key={`wgl-${i}`} id={`wgl-${i}`}>
                          {(handle) => (
                            <div className={`rounded-md border border-border/60 bg-background/40 p-2 space-y-2 transition-opacity ${anchor.enabled === false ? "opacity-50" : ""}`}>
                              <div className="flex items-center gap-2">
                                <DragHandle {...handle} />
                                <ColorSwatch hex={anchor.hex} onChange={(h) => patchWebglAnchor(i, { hex: h })} />
                                <Input
                                  value={anchor.hex}
                                  onChange={(e) => patchWebglAnchor(i, { hex: e.target.value })}
                                  className="h-8 font-mono text-xs"
                                />
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7"
                                  onClick={() => patchWebglAnchor(i, { enabled: anchor.enabled === false })}
                                  aria-label={anchor.enabled === false ? "Enable color" : "Disable color"}
                                  title={anchor.enabled === false ? "Enable color" : "Disable color"}
                                >
                                  {anchor.enabled === false ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                                </Button>
                                <span className="ml-auto text-xs text-muted-foreground">#{i + 1}</span>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-[10px]">Opacity {Math.round(anchor.opacity * 100)}%</Label>
                                  <Slider value={[anchor.opacity * 100]} min={0} max={100} step={1} onValueChange={(v) => patchWebglAnchor(i, { opacity: v[0] / 100 })} />
                                </div>
                                <div>
                                  <Label className="text-[10px]">Reach {(anchor.size ?? 1).toFixed(2)}×</Label>
                                  <Slider value={[(anchor.size ?? 1) * 100]} min={20} max={300} step={5} onValueChange={(v) => patchWebglAnchor(i, { size: v[0] / 100 })} />
                                </div>
                                <div>
                                  <Label className="text-[10px]">X {Math.round(anchor.x * 100)}%</Label>
                                  <Slider value={[anchor.x * 100]} min={-20} max={120} step={1} onValueChange={(v) => patchWebglAnchor(i, { x: v[0] / 100 })} />
                                </div>
                                <div>
                                  <Label className="text-[10px]">Y {Math.round(anchor.y * 100)}%</Label>
                                  <Slider value={[anchor.y * 100]} min={-20} max={120} step={1} onValueChange={(v) => patchWebglAnchor(i, { y: v[0] / 100 })} />
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => resetWebglAnchorPosition(i)}>
                                  Reset position
                                </Button>
                              </div>
                            </div>
                          )}
                        </Sortable>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 pt-2">
                  <div>
                    <Label className="text-[11px]">Speed {editing.webgl.speed}/10</Label>
                    <Slider value={[editing.webgl.speed]} min={1} max={10} step={1} onValueChange={(v) => patchWebgl({ speed: v[0] })} />
                  </div>
                  <div>
                    <Label className="text-[11px]">Intensity {Math.round(editing.webgl.intensity * 100)}%</Label>
                    <Slider value={[editing.webgl.intensity * 100]} min={0} max={100} step={1} onValueChange={(v) => patchWebgl({ intensity: v[0] / 100 })} />
                    <p className="text-[10px] text-muted-foreground mt-1">How much the mesh warps and swirls.</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Color slots (8)</Label>
                <p className="text-[11px] text-muted-foreground">Drag the handles to reorder — later slots render on top.</p>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSlotsDragEnd}>
                  <SortableContext items={editing.slots.map((_, i) => `slot-${i}`)} strategy={rectSortingStrategy}>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      {editing.slots.map((slot, i) => (
                        <Sortable key={`slot-${i}`} id={`slot-${i}`}>
                          {(handle) => (
                            <div className="rounded-md border border-border p-3 space-y-3 bg-background/40">
                              <div className="flex items-center gap-2">
                                <DragHandle {...handle} />
                                <Switch
                                  checked={slot.enabled}
                                  onCheckedChange={(v) => patchSlot(i, { enabled: v })}
                                />
                                <ColorSwatch hex={slot.hex} onChange={(hex) => patchSlot(i, { hex })} />
                                <Input
                                  value={slot.hex}
                                  onChange={(e) => patchSlot(i, { hex: e.target.value })}
                                  className="h-8 font-mono text-xs"
                                />
                                <span className="ml-auto text-xs text-muted-foreground">#{i + 1}</span>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-[11px]">Size {slot.size}%</Label>
                                  <Slider value={[slot.size]} min={20} max={100} step={1} onValueChange={(v) => patchSlot(i, { size: v[0] })} />
                                </div>
                                <div>
                                  <Label className="text-[11px]">Opacity {Math.round(slot.opacity * 100)}%</Label>
                                  <Slider value={[slot.opacity * 100]} min={0} max={100} step={1} onValueChange={(v) => patchSlot(i, { opacity: v[0] / 100 })} />
                                </div>
                                <div>
                                  <Label className="text-[11px]">X {slot.x}%</Label>
                                  <Slider value={[slot.x]} min={-30} max={100} step={1} onValueChange={(v) => patchSlot(i, { x: v[0] })} />
                                </div>
                                <div>
                                  <Label className="text-[11px]">Y {slot.y}%</Label>
                                  <Slider value={[slot.y]} min={-30} max={100} step={1} onValueChange={(v) => patchSlot(i, { y: v[0] })} />
                                </div>
                                <div>
                                  <Label className="text-[11px]">Aspect {(slot.aspect ?? 1).toFixed(2)}</Label>
                                  <Slider value={[(slot.aspect ?? 1) * 100]} min={30} max={300} step={5} onValueChange={(v) => patchSlot(i, { aspect: v[0] / 100 })} />
                                </div>
                                <div>
                                  <Label className="text-[11px]">Rotation {slot.rotation ?? 0}°</Label>
                                  <Slider value={[slot.rotation ?? 0]} min={0} max={360} step={5} onValueChange={(v) => patchSlot(i, { rotation: v[0] })} />
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-[11px]">Shape</Label>
                                  <Select value={slot.shape ?? editing.type} onValueChange={(v) => patchSlot(i, { shape: v as MeshGradientType })}>
                                    <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      {SHAPE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className="text-[11px]">Blend</Label>
                                  <Select value={slot.blend ?? editing.blend} onValueChange={(v) => patchSlot(i, { blend: v as MeshBlendMode })}>
                                    <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      {BLEND_OPTIONS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            </div>
                          )}
                        </Sortable>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

              </div>
            )}

            {/* Global defaults (only meaningful for CSS engine) */}
            {!isWebgl && (
              <>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Label>Default shape (for new slots)</Label>
                    <Select value={editing.type} onValueChange={(v) => patchEditing({ type: v as MeshGradientType })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SHAPE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Default blend mode</Label>
                    <Select value={editing.blend} onValueChange={(v) => patchEditing({ blend: v as MeshBlendMode })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {BLEND_OPTIONS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Label>Animation speed ({editing.speed}/10 — {speedToDurationSec(editing.speed)}s cycle)</Label>
                    <Slider value={[editing.speed]} min={1} max={10} step={1} onValueChange={(v) => patchEditing({ speed: v[0] })} />
                  </div>
                  <div>
                    <Label>Smoothing / blur ({editing.blur}px)</Label>
                    <Slider value={[editing.blur]} min={20} max={150} step={1} onValueChange={(v) => patchEditing({ blur: v[0] })} />
                  </div>
                </div>
              </>
            )}

            {/* Grain */}
            <div className="rounded-md border border-border p-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Film grain</Label>
                  <p className="text-[11px] text-muted-foreground">Subtle noise overlay. Every premium gradient has this.</p>
                </div>
                <Switch checked={editing.grain.enabled} onCheckedChange={(v) => patchGrain({ enabled: v })} />
              </div>
              {editing.grain.enabled && (
                <div className="mt-3">
                  <Label className="text-[11px]">Intensity {Math.round(editing.grain.intensity * 100)}%</Label>
                  <Slider value={[editing.grain.intensity * 100]} min={1} max={20} step={1} onValueChange={(v) => patchGrain({ intensity: v[0] / 100 })} />
                </div>
              )}
            </div>

            {/* Shape mask */}
            <ShapeMaskSection
              shape={editing.shape ?? DEFAULT_SHAPE_MASK}
              onPatch={patchShape}
              onUpload={handleSvgUpload}
            />



            {/* Overlay */}
            <div className="rounded-md border border-border p-3">
              <Label>Overlay tint</Label>
              <p className="text-[11px] text-muted-foreground">Flat color laid over the whole mesh. Use to mute or warm the field.</p>
              <div className="mt-3 flex items-center gap-2">
                <ColorSwatch hex={editing.overlay.hex} onChange={(hex) => patchEditing({ overlay: { ...editing.overlay, hex } })} />
                <Input
                  value={editing.overlay.hex}
                  onChange={(e) => patchEditing({ overlay: { ...editing.overlay, hex: e.target.value } })}
                  className="h-8 font-mono text-xs max-w-[140px]"
                />
                <div className="flex-1">
                  <Label className="text-[11px]">Opacity {Math.round(editing.overlay.opacity * 100)}%</Label>
                  <Slider
                    value={[editing.overlay.opacity * 100]}
                    min={0} max={100} step={1}
                    onValueChange={(v) => patchEditing({ overlay: { ...editing.overlay, opacity: v[0] / 100 } })}
                  />
                </div>
              </div>
            </div>

            {/* Mobile-only overrides (applied when viewport <768px on the live site) */}
            {(() => {
              const mob = editing.mobile ?? DEFAULT_MOBILE_OVERRIDE;
              return (
                <div className="rounded-md border border-border p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Mobile overrides</Label>
                      <p className="text-[11px] text-muted-foreground">
                        Applied only on phones (&lt;768px), where the heart uses the CSS blob renderer instead of WebGL.
                        Preview pane shows the desktop look — resize the browser narrow or open on a phone to verify.
                      </p>
                    </div>
                    <Switch
                      checked={!!editing.mobile?.enabled}
                      onCheckedChange={(v) => patchMobile({ enabled: v })}
                    />
                  </div>
                  {editing.mobile?.enabled && (
                    <div className="mt-3 space-y-3">
                      <div>
                        <Label className="text-[11px]">Mobile base color</Label>
                        <div className="mt-1 flex items-center gap-2">
                          <ColorSwatch
                            hex={mob.baseColor ?? editing.baseColor}
                            onChange={(hex) => patchMobile({ baseColor: hex })}
                          />
                          <Input
                            value={mob.baseColor ?? editing.baseColor}
                            onChange={(e) => patchMobile({ baseColor: e.target.value })}
                            className="h-8 font-mono text-xs max-w-[140px]"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[11px]">Mobile blend mode</Label>
                        <Select
                          value={mob.blend ?? editing.blend}
                          onValueChange={(v) => patchMobile({ blend: v as MeshBlendMode })}
                        >
                          <SelectTrigger className="h-8 mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {BLEND_OPTIONS.map((b) => (
                              <SelectItem key={b} value={b}>{b}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[11px]">
                          Opacity multiplier {Math.round((mob.opacityMultiplier ?? 1) * 100)}%
                        </Label>
                        <Slider
                          value={[(mob.opacityMultiplier ?? 1) * 100]}
                          min={10} max={100} step={5}
                          onValueChange={(v) => patchMobile({ opacityMultiplier: v[0] / 100 })}
                        />
                      </div>
                      <div>
                        <Label className="text-[11px]">
                          Blur delta {(mob.blurDelta ?? 0) >= 0 ? "+" : ""}{mob.blurDelta ?? 0}px
                        </Label>
                        <Slider
                          value={[(mob.blurDelta ?? 0) + 50]}
                          min={0} max={100} step={1}
                          onValueChange={(v) => patchMobile({ blurDelta: v[0] - 50 })}
                        />
                      </div>

                      {/* Per-slot mobile color overrides */}
                      <div className="pt-2 border-t border-border">
                        <Label className="text-[11px]">Mobile slot colors</Label>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Override individual CSS-blob slot colors on mobile only. Leave blank to inherit the desktop slot color.
                        </p>
                        <div className="mt-2 space-y-1.5">
                          {editing.slots.map((slot, i) => {
                            const hexes = mob.slotHexes ?? [];
                            const raw = hexes[i];
                            const overrideHex = typeof raw === "string" ? raw : "";
                            const effective = overrideHex.trim() || slot.hex;
                            const setHex = (next: string | null) => {
                              const arr = [...(mob.slotHexes ?? [])];
                              while (arr.length < 8) arr.push(null);
                              arr[i] = next;
                              patchMobile({ slotHexes: arr });
                            };
                            return (
                              <div key={i} className="flex items-center gap-2">
                                <span className="text-[11px] text-muted-foreground w-6">#{i + 1}</span>
                                <ColorSwatch hex={effective} onChange={(hex) => setHex(hex)} />
                                <Input
                                  value={overrideHex}
                                  placeholder={slot.hex}
                                  onChange={(e) => setHex(e.target.value || null)}
                                  className="h-7 font-mono text-xs max-w-[120px]"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-[11px]"
                                  disabled={!overrideHex}
                                  onClick={() => setHex(null)}
                                >
                                  Reset
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                    </div>
                  )}
                </div>
              );
            })()}

          </CardContent>
        </Card>
  );

  return (
    <>
      {/* Mobile: stacked */}
      <div className="space-y-4 md:hidden">
        {previewCard}
        {presetsCard}
        {editorCard}
      </div>

      {/* Desktop: 3-column resizable, preview sticky to top */}
      <div className="hidden md:block h-[calc(100vh-220px)] min-h-[600px]">
        <ResizablePanelGroup direction="horizontal" autoSaveId="mesh-tab-cols" className="h-full">
          <ResizablePanel defaultSize={18} minSize={12} maxSize={32}>
            <div className="h-full overflow-y-auto pr-2">{presetsCard}</div>
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={52} minSize={30}>
            <div className="h-full overflow-y-auto px-2">{editorCard}</div>
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={30} minSize={22} maxSize={50}>
            <div className="h-full overflow-y-auto pl-2">
              <div className="sticky top-0">{previewCard}</div>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </>
  );
};

const ShapeMaskSection = ({
  shape,
  onPatch,
  onUpload,
}: {
  shape: ShapeMaskConfig;
  onPatch: (p: Partial<ShapeMaskConfig>) => void;
  onUpload: (file: File) => void;
}) => {
  const fileId = "shape-svg-upload";
  return (
    <div className="rounded-md border border-border p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Label>SVG shape mask</Label>
          <p className="text-[11px] text-muted-foreground">
            Constrain the mesh to a shape, or lay a shape on top — useful for sophisticated, focused backgrounds.
          </p>
        </div>
        <Switch checked={shape.enabled} onCheckedChange={(v) => onPatch({ enabled: v })} />
      </div>

      {shape.enabled && (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label className="text-[11px]">Mode</Label>
              <Select value={shape.mode} onValueChange={(v) => onPatch({ mode: v as ShapeMaskMode })}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SHAPE_MODE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[10px] text-muted-foreground">
                {SHAPE_MODE_OPTIONS.find((o) => o.value === shape.mode)?.description}
              </p>
            </div>
            <div>
              <Label className="text-[11px]">Shape</Label>
              <Select value={shape.preset} onValueChange={(v) => onPatch({ preset: v as ShapeMaskPreset })}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SHAPE_PRESET_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Upload control */}
          <div className="rounded border border-dashed border-border p-2 flex items-center gap-2">
            <input
              id={fileId}
              type="file"
              accept=".svg,image/svg+xml"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onUpload(f);
                e.target.value = "";
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => document.getElementById(fileId)?.click()}
            >
              <Upload className="h-3.5 w-3.5 mr-1" /> Upload SVG
            </Button>
            {shape.preset === "custom" && shape.customSvg ? (
              <>
                <span className="text-[11px] text-muted-foreground">
                  Custom SVG loaded ({Math.round(shape.customSvg.length / 1024 * 10) / 10} KB)
                </span>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 ml-auto"
                  onClick={() => onPatch({ preset: "heart", customSvg: undefined })}
                  aria-label="Remove custom SVG"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </>
            ) : (
              <span className="text-[11px] text-muted-foreground">Max 50 KB · .svg only</span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px]">X {shape.x}%</Label>
              <Slider value={[shape.x]} min={0} max={100} step={1} onValueChange={(v) => onPatch({ x: v[0] })} />
            </div>
            <div>
              <Label className="text-[11px]">Y {shape.y}%</Label>
              <Slider value={[shape.y]} min={0} max={100} step={1} onValueChange={(v) => onPatch({ y: v[0] })} />
            </div>
            <div>
              <Label className="text-[11px]">Size {shape.size}%</Label>
              <Slider value={[shape.size]} min={10} max={200} step={1} onValueChange={(v) => onPatch({ size: v[0] })} />
            </div>
            <div>
              <Label className="text-[11px]">Rotation {shape.rotation}°</Label>
              <Slider value={[shape.rotation]} min={0} max={360} step={5} onValueChange={(v) => onPatch({ rotation: v[0] })} />
            </div>
            <div>
              <Label className="text-[11px]">Feather {shape.feather}px</Label>
              <Slider value={[shape.feather]} min={0} max={80} step={1} onValueChange={(v) => onPatch({ feather: v[0] })} />
            </div>
            <div>
              <Label className="text-[11px]">Intensity {Math.round(shape.intensity * 100)}%</Label>
              <Slider value={[shape.intensity * 100]} min={0} max={100} step={1} onValueChange={(v) => onPatch({ intensity: v[0] / 100 })} />
              <p className="text-[10px] text-muted-foreground mt-1">Used by silhouette + vignette modes.</p>
            </div>
          </div>

          <div className="rounded border border-border/60 p-2 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[11px]">Halo glow (mask mode only)</Label>
              <Switch
                checked={shape.halo.enabled}
                onCheckedChange={(v) => onPatch({ halo: { ...shape.halo, enabled: v } })}
              />
            </div>
            {shape.halo.enabled && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px]">Halo size {shape.halo.size.toFixed(2)}×</Label>
                    <Slider
                      value={[shape.halo.size * 100]}
                      min={100} max={250} step={5}
                      onValueChange={(v) => onPatch({ halo: { ...shape.halo, size: v[0] / 100 } })}
                    />
                  </div>
                  <div>
                    <Label className="text-[10px]">Halo opacity {Math.round(shape.halo.intensity * 100)}%</Label>
                    <Slider
                      value={[shape.halo.intensity * 100]}
                      min={0} max={100} step={1}
                      onValueChange={(v) => onPatch({ halo: { ...shape.halo, intensity: v[0] / 100 } })}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px]">Halo color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <ColorSwatch
                      hex={shape.halo.color}
                      onChange={(hex) => onPatch({ halo: { ...shape.halo, color: hex } })}
                    />
                    <Input
                      value={shape.halo.color}
                      onChange={(e) => onPatch({ halo: { ...shape.halo, color: e.target.value } })}
                      className="h-8 font-mono text-xs"
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="rounded border border-border/60 p-2 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[11px]">Extend mesh outside shape (mask mode only)</Label>
              <Switch
                checked={shape.extendOutside.enabled}
                onCheckedChange={(v) =>
                  onPatch({ extendOutside: { ...shape.extendOutside, enabled: v } })
                }
              />
            </div>
            <p className="text-[10px] text-muted-foreground">
              Paints the same mesh (renderer + motion) full-bleed behind the shape so the area outside isn't flat base color.
            </p>
            {shape.extendOutside.enabled && (
              <div>
                <Label className="text-[10px]">
                  Outside intensity {Math.round(shape.extendOutside.intensity * 100)}%
                </Label>
                <Slider
                  value={[shape.extendOutside.intensity * 100]}
                  min={0}
                  max={100}
                  step={1}
                  onValueChange={(v) =>
                    onPatch({ extendOutside: { ...shape.extendOutside, intensity: v[0] / 100 } })
                  }
                />
              </div>
            )}
          </div>



        </>
      )}
    </div>
  );
};
